import React, { useState, useEffect } from 'react';
import { getRaces, getCurrentSeason, getAllSeasons } from '../services/api';
import { Card, CardContent } from './ui/card';
import { MapPin, Clock, ChevronDown, ChevronUp, Flag } from 'lucide-react';
import { getCircuitLayoutUrl, FILTER_GOLD, FILTER_DARK_BLUE, LOCAL_PNG_GOLD_FILTER, LOCAL_PNG_DARK_BLUE_FILTER } from '../utils/circuitLayouts';
import { getRaceFlagUrl } from '../utils/countryFlags';
import { useTheme } from '../contexts/ThemeContext';
import { formatLocalTime, formatLocalDate, formatLocalFullDate } from '../utils/timeUtils';

const RaceCalendar = () => {
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRace, setExpandedRace] = useState(null);
  const [currentSeasonYear, setCurrentSeasonYear] = useState(null);
  const { theme } = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First try to get the current active season
        let seasonYear = null;
        try {
          const currentSeason = await getCurrentSeason();
          if (currentSeason && currentSeason.year) {
            seasonYear = currentSeason.year;
          }
        } catch (e) {
          console.log('No active season found, checking all seasons');
        }
        
        // If no active season, get the most recent season
        if (!seasonYear) {
          const allSeasons = await getAllSeasons();
          if (allSeasons && allSeasons.length > 0) {
            // Sort by year descending and get the first one
            const sortedSeasons = allSeasons.sort((a, b) => b.year - a.year);
            seasonYear = sortedSeasons[0].year;
          }
        }
        
        // Fallback to 2026 if nothing found
        if (!seasonYear) {
          seasonYear = 2026;
        }
        
        setCurrentSeasonYear(seasonYear);
        const data = await getRaces(seasonYear);
        setRaces(data);
      } catch (error) {
        console.error('Error fetching races:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateString, timeString) => {
    return formatLocalDate(dateString, timeString);
  };

  const formatFullDate = (dateString, timeString) => {
    return formatLocalFullDate(dateString, timeString);
  };

  const formatTime = (dateStr, timeStr) => {
    if (!timeStr || timeStr === '') return 'TBD';
    return formatLocalTime(dateStr, timeStr);
  };

  const toggleExpand = (raceId) => {
    setExpandedRace(expandedRace === raceId ? null : raceId);
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Laden...</div>;
  }

  if (races.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Geen races gevonden voor het huidige seizoen.</p>
        {currentSeasonYear && (
          <p className="text-sm text-muted-foreground mt-2">Seizoen: {currentSeasonYear}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {currentSeasonYear && (
        <div className="text-sm text-muted-foreground mb-4 text-center">
          Racekalender {currentSeasonYear}
        </div>
      )}
      {races.map((race, index) => {
        const circuitUrl = getCircuitLayoutUrl(race);
        const flagUrl = getRaceFlagUrl(race);
        const isExpanded = expandedRace === race.race_id;
        const isUpcoming = race.status === 'upcoming';
        
        return (
          <Card
            key={race.race_id || index}
            className={`border-l-4 transition-all duration-200 overflow-hidden relative ${
              isUpcoming ? 'border-l-[#FDB913]' : 'border-l-muted-foreground'
            } ${isExpanded ? 'shadow-lg' : 'hover:shadow-md'}`}
          >
            {/* Transparent Flag Background - Full Width */}
            {flagUrl && (
              <div 
                className="absolute inset-0 pointer-events-none overflow-hidden"
                style={{ opacity: 0.1 }}
              >
                <img 
                  src={flagUrl} 
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            
            {/* Compact Header - Always Visible */}
            <div 
              className="flex items-center justify-between p-3 cursor-pointer hover:bg-secondary/50 relative z-10"
              onClick={() => toggleExpand(race.race_id)}
            >
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                {/* Round Number */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  isUpcoming ? 'bg-[#FDB913] text-[#1a2332]' : 'bg-muted text-muted-foreground'
                }`}>
                  R{race.round}
                </div>
                
                {/* Circuit Icon */}
                {circuitUrl && (
                  <img 
                    src={circuitUrl} 
                    alt=""
                    className="w-10 h-8 object-contain flex-shrink-0 hidden sm:block"
                    style={{ 
                      filter: (circuitUrl.includes('/circuits/') || circuitUrl.includes('/uploads/'))
                        ? (theme === 'dark' ? LOCAL_PNG_GOLD_FILTER : LOCAL_PNG_DARK_BLUE_FILTER)
                        : (theme === 'dark' ? FILTER_GOLD : FILTER_DARK_BLUE)
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                
                {/* Race Name & Location */}
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-[#FDB913] truncate text-sm sm:text-base">{race.name}</div>
                  <div className="text-xs text-muted-foreground flex items-center">
                    <MapPin size={10} className="mr-1 flex-shrink-0" />
                    <span className="truncate">{race.location}, {race.country}</span>
                  </div>
                </div>
              </div>
              
              {/* Race Date & Time - Compact */}
              <div className="flex items-center space-x-3 flex-shrink-0 ml-2">
                <div className="text-right hidden sm:block">
                  <div className="text-sm font-semibold text-foreground">{formatDate(race.date, race.time)}</div>
                  <div className="text-xs text-[#FDB913] flex items-center justify-end">
                    <Clock size={10} className="mr-1" />
                    {formatTime(race.date, race.time)}
                  </div>
                </div>
                
                {/* Status Badge */}
                <div className={`px-2 py-0.5 rounded text-xs font-semibold hidden md:block ${
                  isUpcoming ? 'bg-[#FDB913] text-[#1a2332]' : 'bg-muted text-muted-foreground'
                }`}>
                  {isUpcoming ? 'KOMEND' : 'KLAAR'}
                </div>
                
                {/* Expand Icon */}
                <div className="text-muted-foreground">
                  {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </div>
            </div>
            
            {/* Expanded Content - Session Details */}
            {isExpanded && (
              <CardContent className="pt-0 pb-4 px-4 border-t">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mt-3">
                  {/* FP1 - Always shown */}
                  <div className="bg-secondary/80 p-2 rounded border border-border">
                    <div className="text-xs text-muted-foreground font-semibold">VT1</div>
                    <div className="text-xs font-medium text-foreground">{formatFullDate(race.fp1?.date, race.fp1?.time)}</div>
                    <div className="text-xs text-[#FDB913] flex items-center">
                      <Clock size={10} className="mr-1" />
                      {formatTime(race.fp1?.date, race.fp1?.time)}
                    </div>
                  </div>

                  {/* Session 2: Sprint Qualifying or FP2 */}
                  {race.is_sprint_weekend ? (
                    <div className="bg-purple-500/10 p-2 rounded border border-purple-500/30">
                      <div className="text-xs text-purple-400 font-semibold">Sprint Kwal.</div>
                      <div className="text-xs font-medium text-foreground">{formatFullDate(race.sprint_qualifying?.date, race.sprint_qualifying?.time)}</div>
                      <div className="text-xs text-purple-400 flex items-center">
                        <Clock size={10} className="mr-1" />
                        {formatTime(race.sprint_qualifying?.date, race.sprint_qualifying?.time)}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-secondary/80 p-2 rounded border border-border">
                      <div className="text-xs text-muted-foreground font-semibold">VT2</div>
                      <div className="text-xs font-medium text-foreground">{formatFullDate(race.fp2?.date, race.fp2?.time)}</div>
                      <div className="text-xs text-[#FDB913] flex items-center">
                        <Clock size={10} className="mr-1" />
                        {formatTime(race.fp2?.date, race.fp2?.time)}
                      </div>
                    </div>
                  )}

                  {/* Session 3: Sprint or FP3 */}
                  {race.is_sprint_weekend ? (
                    <div className="bg-purple-500/10 p-2 rounded border border-purple-500/30">
                      <div className="text-xs text-purple-400 font-semibold">Sprint</div>
                      <div className="text-xs font-medium text-foreground">{formatFullDate(race.sprint?.date, race.sprint?.time)}</div>
                      <div className="text-xs text-purple-400 flex items-center">
                        <Clock size={10} className="mr-1" />
                        {formatTime(race.sprint?.date, race.sprint?.time)}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-secondary/80 p-2 rounded border border-border">
                      <div className="text-xs text-muted-foreground font-semibold">VT3</div>
                      <div className="text-xs font-medium text-foreground">{formatFullDate(race.fp3?.date, race.fp3?.time)}</div>
                      <div className="text-xs text-[#FDB913] flex items-center">
                        <Clock size={10} className="mr-1" />
                        {formatTime(race.fp3?.date, race.fp3?.time)}
                      </div>
                    </div>
                  )}

                  {/* Qualifying */}
                  <div className="bg-secondary/80 p-2 rounded border border-border">
                    <div className="text-xs text-muted-foreground font-semibold">Kwalificatie</div>
                    <div className="text-xs font-medium text-foreground">{formatFullDate(race.qualifying?.date, race.qualifying?.time)}</div>
                    <div className="text-xs text-[#FDB913] flex items-center">
                      <Clock size={10} className="mr-1" />
                      {formatTime(race.qualifying?.date, race.qualifying?.time)}
                    </div>
                  </div>

                  {/* Race */}
                  <div className="bg-gradient-to-br from-[#FDB913] to-[#e5a911] p-2 rounded border-2 border-[#FDB913]">
                    <div className="text-xs text-[#1a2332] font-bold flex items-center">
                      <Flag size={10} className="mr-1" />
                      RACE
                    </div>
                    <div className="text-xs font-bold text-[#1a2332]">{formatFullDate(race.date, race.time)}</div>
                    <div className="text-xs text-[#1a2332] flex items-center font-semibold">
                      <Clock size={10} className="mr-1" />
                      {formatTime(race.date, race.time)}
                    </div>
                  </div>
                </div>
                
                {/* Sprint Weekend indicator */}
                {race.is_sprint_weekend && (
                  <div className="mt-2 text-xs text-purple-400 font-semibold flex items-center">
                    <span className="bg-purple-500/20 px-2 py-0.5 rounded">Sprint Weekend</span>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default RaceCalendar;
